import { endReducer } from '@/utils/end-reducer';
import {
  ItemsContext_VariantsPackFieldsFragment,
  SmartOrderResponseFieldsFragment,
} from '@/types';
import { QuantityMapType } from '@/screens/new-order/hooks/use-quantity-reducer';
import { useReducer } from 'react';

export interface GroupedSmartOrderResult {
  quantity: number;
  searchTerm: string;
  variantPacks: ItemsContext_VariantsPackFieldsFragment[];
}

export interface SmartOrderState {
  groupedResults: GroupedSmartOrderResult[];
  isVPOutOfStock: (variantPackUUID: string) => boolean;
  quantityMap: QuantityMapType;
  requestedItems: string;
}

export enum SmartOrderAction {
  ADD_ITEM = 'ADD_ITEM',
  EDIT_ITEM = 'EDIT_ITEM',
  GROUP_INITIAL_RESULTS = 'GROUP_INITIAL_RESULTS',
  SET_REQUESTED_ITEMS = 'SET_REQUESTED_ITEMS',
  UPDATE_QUANTITY = 'UPDATE_QUANTITY',
}

export type Action =
  | {
      payload: {
        results: SmartOrderResponseFieldsFragment[];
      };
      type: SmartOrderAction.ADD_ITEM;
    }
  | {
      payload: {
        groupIndex: number;
        results: SmartOrderResponseFieldsFragment[];
      };
      type: SmartOrderAction.EDIT_ITEM;
    }
  | {
      payload: {
        results: SmartOrderResponseFieldsFragment[];
      };
      type: SmartOrderAction.GROUP_INITIAL_RESULTS;
    }
  | {
      payload: {
        requestedItems: string;
      };
      type: SmartOrderAction.SET_REQUESTED_ITEMS;
    }
  | {
      payload: {
        quantity: number;
        variantPackUUID: string;
      };
      type: SmartOrderAction.UPDATE_QUANTITY;
    };

export const initialSmartOrderState: SmartOrderState = {
  groupedResults: [],
  isVPOutOfStock: () => false,
  quantityMap: {},
  requestedItems: '',
};

const groupSmartOrderResults = (
  results: SmartOrderResponseFieldsFragment[],
  quantityMap: QuantityMapType,
  isVPOutOfStock: (variantPackUUID: string) => boolean,
) => {
  const groupedResults = results
    .reduce<GroupedSmartOrderResult[]>((acc, result) => {
      const variantPack = result.variant_pack;
      const lastGroupedResult = acc[acc.length - 1];

      if (
        acc.length === 0 ||
        lastGroupedResult.searchTerm !== result.search_term
      ) {
        acc.push({
          quantity: result.quantity,
          searchTerm: result.search_term,
          variantPacks: variantPack ? [variantPack] : [],
        });
      } else if (variantPack) {
        lastGroupedResult.variantPacks.push(variantPack);
      }

      return acc;
    }, [])
    // Ensure first item within group is available and set quantity
    .map((group) => {
      if (group.variantPacks.length > 0) {
        const firstVPUUID = group.variantPacks[0].uuid;
        const isFirstVPOutOfStock = isVPOutOfStock(firstVPUUID);

        if (isFirstVPOutOfStock) {
          const nextAvailableVPIndex = group.variantPacks.findIndex(
            (vp) => !isVPOutOfStock(vp.uuid),
          );
          // Update first index with next available variant pack
          if (nextAvailableVPIndex !== -1) {
            const [nextAvailableVP] = group.variantPacks.splice(
              nextAvailableVPIndex,
              1,
            );
            quantityMap[nextAvailableVP.uuid] = group.quantity;

            return {
              ...group,
              variantPacks: [nextAvailableVP, ...group.variantPacks],
            };
          }
        } else {
          quantityMap[firstVPUUID] = group.quantity;
        }
      }

      return group;
    });

  return {
    groupedResults,
    quantityMap,
  };
};

const reducer = (state: SmartOrderState, action: Action) => {
  switch (action.type) {
    case SmartOrderAction.ADD_ITEM:
      const addedGroupedResults = groupSmartOrderResults(
        action.payload.results,
        { ...state.quantityMap },
        state.isVPOutOfStock,
      );

      return {
        ...state,
        groupedResults: [
          ...state.groupedResults,
          addedGroupedResults.groupedResults[0],
        ],
        quantityMap: addedGroupedResults.quantityMap,
      };
    case SmartOrderAction.EDIT_ITEM:
      const editedGroup = state.groupedResults[action.payload.groupIndex];
      const newQuantityMap = { ...state.quantityMap };

      // Remove any quantities of edited group's variant packs
      for (const vp of editedGroup.variantPacks) {
        if (newQuantityMap[vp.uuid]) {
          delete newQuantityMap[vp.uuid];
        }
      }

      const editedGroupedResults = groupSmartOrderResults(
        action.payload.results,
        newQuantityMap,
        state.isVPOutOfStock,
      );

      return {
        ...state,
        groupedResults: state.groupedResults.map((group, i) =>
          i === action.payload.groupIndex
            ? editedGroupedResults.groupedResults[0]
            : group,
        ),
        quantityMap: editedGroupedResults.quantityMap,
      };
    case SmartOrderAction.GROUP_INITIAL_RESULTS:
      return {
        ...state,
        ...groupSmartOrderResults(
          action.payload.results,
          {},
          state.isVPOutOfStock,
        ),
      };
    case SmartOrderAction.SET_REQUESTED_ITEMS:
      return {
        ...state,
        requestedItems: action.payload.requestedItems,
      };
    case SmartOrderAction.UPDATE_QUANTITY:
      const { quantity, variantPackUUID } = action.payload;
      return {
        ...state,
        quantityMap: { ...state.quantityMap, [variantPackUUID]: quantity },
      };
    default:
      return endReducer(state, action);
  }
};

export const useSmartOrderReducer = (
  isVPOutOfStock: (variantPackUUID: string) => boolean,
  state: SmartOrderState = initialSmartOrderState,
) =>
  useReducer(reducer, {
    ...state,
    isVPOutOfStock,
  });
